import initSqlJs, { Database } from 'sql.js';

let db: Database | null = null;

export async function initializeDatabase(): Promise<Database> {
  if (db) return db;

  try {
    const SQL = await initSqlJs({
      locateFile: () => {
        // For GitHub Pages deployment, the base path is /ce-marking-guide/
        const basePath = window.location.pathname.includes('/ce-marking-guide/')
          ? '/ce-marking-guide/'
          : '/';
        return `${basePath}sql-wasm.wasm`;
      },
    });

    db = new SQL.Database();

    // Create tables
    createTables();

    // Insert initial data
    insertInitialData();

    return db;
  } catch (error) {
    console.error('Failed to initialize database:', error);
    throw error;
  }
}

function createTables() {
  if (!db) return;

  // User sessions table
  db.run(`
    CREATE TABLE IF NOT EXISTS user_sessions (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      session_id TEXT UNIQUE NOT NULL,
      created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
      updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
    )
  `);

  // Questionnaire answers table
  db.run(`
    CREATE TABLE IF NOT EXISTS questionnaire_answers (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      session_id TEXT NOT NULL,
      question_id TEXT NOT NULL,
      answer_value TEXT,
      answer_type TEXT,
      created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
      FOREIGN KEY (session_id) REFERENCES user_sessions (session_id)
    )
  `);

  // Directives table
  db.run(`
    CREATE TABLE IF NOT EXISTS directives (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      directive_id TEXT UNIQUE NOT NULL,
      name TEXT NOT NULL,
      description TEXT,
      url TEXT,
      is_applicable BOOLEAN DEFAULT FALSE,
      created_at DATETIME DEFAULT CURRENT_TIMESTAMP
    )
  `);

  // Standards table
  db.run(`
    CREATE TABLE IF NOT EXISTS standards (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      standard_id TEXT UNIQUE NOT NULL,
      name TEXT NOT NULL,
      description TEXT,
      directive_id TEXT,
      url TEXT,
      created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
      FOREIGN KEY (directive_id) REFERENCES directives (directive_id)
    )
  `);

  // Reports table
  db.run(`
    CREATE TABLE IF NOT EXISTS reports (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      session_id TEXT NOT NULL,
      report_data TEXT,
      generated_at DATETIME DEFAULT CURRENT_TIMESTAMP,
      FOREIGN KEY (session_id) REFERENCES user_sessions (session_id)
    )
  `);
}

function insertInitialData() {
  if (!db) return;

  // Insert EU Directives
  const directives = [
    [
      '2006/42/EC',
      'Machinery Directive',
      'Safety requirements for machinery and safety components',
      'https://eur-lex.europa.eu/eli/dir/2006/42/oj/eng',
    ],
    [
      '2014/35/EU',
      'Low Voltage Directive',
      'Safety requirements for electrical equipment',
      'https://eur-lex.europa.eu/eli/dir/2014/35/oj/eng',
    ],
    [
      '2014/30/EU',
      'Electromagnetic Compatibility Directive',
      'Requirements for electromagnetic compatibility',
      'https://eur-lex.europa.eu/eli/dir/2014/30/oj/eng',
    ],
    [
      '2014/68/EU',
      'Pressure Equipment Directive',
      'Safety requirements for pressure equipment',
      'https://eur-lex.europa.eu/eli/dir/2014/68/oj/eng',
    ],
    [
      '2014/34/EU',
      'ATEX Directive',
      'Equipment for explosive atmospheres',
      'https://eur-lex.europa.eu/eli/dir/2014/34/oj/eng',
    ],
    [
      '2014/33/EU',
      'Lifts Directive',
      'Safety requirements for lifts and safety components',
      'https://eur-lex.europa.eu/eli/dir/2014/33/oj/eng',
    ],
  ];

  directives.forEach(([id, name, description, url]) => {
    db!.run(
      'INSERT OR IGNORE INTO directives (directive_id, name, description, url) VALUES (?, ?, ?, ?)',
      [id, name, description, url],
    );
  });

  // Insert Harmonized Standards
  const standards = [
    [
      'EN ISO 12100',
      'Safety of machinery - General principles for design',
      'Risk assessment and risk reduction',
      '2006/42/EC',
      'https://standards.cencenelec.eu/dyn/www/f?p=204:110:0::::FSP_PROJECT:71885',
    ],
    [
      'EN 60204-1',
      'Safety of machinery - Electrical equipment of machines',
      'General requirements for electrical equipment',
      '2006/42/EC',
      'https://standards.cencenelec.eu/dyn/www/f?p=204:110:0::::FSP_PROJECT:71886',
    ],
    [
      'EN 61000-6-1',
      'Electromagnetic compatibility (EMC)',
      'Generic standards - Immunity for residential environments',
      '2014/30/EU',
      'https://standards.cencenelec.eu/dyn/www/f?p=204:110:0::::FSP_PROJECT:71887',
    ],
    [
      'EN 61000-6-3',
      'Electromagnetic compatibility (EMC)',
      'Generic standards - Emission standard for residential environments',
      '2014/30/EU',
      'https://standards.cencenelec.eu/dyn/www/f?p=204:110:0::::FSP_PROJECT:71888',
    ],
    [
      'EN 60079-0',
      'Explosive atmospheres',
      'Equipment - General requirements',
      '2014/34/EU',
      'https://standards.cencenelec.eu/dyn/www/f?p=204:110:0::::FSP_PROJECT:71889',
    ],
  ];

  standards.forEach(([id, name, description, directiveId, url]) => {
    db!.run(
      'INSERT OR IGNORE INTO standards (standard_id, name, description, directive_id, url) VALUES (?, ?, ?, ?, ?)',
      [id, name, description, directiveId, url],
    );
  });
}

export function getDatabase(): Database | null {
  return db;
}

export function closeDatabase() {
  if (db) {
    db.close();
    db = null;
  }
}
